import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Nav from "../components/Nav";
import Logo from "../components/Logo";
import RealityTeaser from "../components/RealityTeaser";
import HeroHUD, { ProductionMarquee, CountUp } from "../components/Motion";
import { IMAGES, SCENARIOS } from "../lib/content";
import { useContent } from "../lib/ContentContext";
import { getCreators } from "../lib/defaults";
import api, { formatApiError } from "../lib/api";

// ---------- HERO ----------
function Hero({ onScrollDown }) {
  const { content } = useContent();
  const t = content.hero;
  return (
    <section
      data-testid="hero"
      className="relative h-[100vh] min-h-[820px] w-full overflow-hidden bg-neutral-900"
    >
      <div
        className="absolute inset-0 kenburns"
        style={{
          backgroundImage: `url(${IMAGES.hero})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/55 to-black/35" />

      {/* Cinematic HUD */}
      <HeroHUD />

      <div className="absolute inset-0 flex flex-col">
        <Nav variant="hero" />

        {/* Faint Alta mark backdrop centered-right */}
        <div className="absolute inset-y-0 right-0 w-[45%] flex items-center justify-end pr-12 pointer-events-none hidden lg:flex">
          <Logo
            variant="light"
            size={520}
            testid="hero-alta-mark"
            className="opacity-[0.09]"
          />
        </div>

        <div className="flex-1 flex items-center">
          <div className="max-w-[1400px] mx-auto w-full px-6 md:px-12 fade-up">
            <div className="max-w-[1000px]">
              {/* Promo pill */}
              <div
                data-testid="hero-pill"
                className="inline-flex items-center gap-3 border border-[color:var(--alta-red)]/70 bg-[color:var(--alta-red)]/15 px-4 py-2 mono text-[10px] uppercase tracking-[0.28em] text-[#F9F8F6] backdrop-blur-sm"
              >
                <span className="rec-dot" aria-hidden="true" />
                <span>{t.pill1}</span>
                <span className="text-[#F9F8F6]/50">/</span>
                <span>{t.pill2}</span>
                <span className="text-[#F9F8F6]/50">/</span>
                <span>{t.pill3}</span>
              </div>

              {/* Eyebrow */}
              <div className="mt-8 mono text-[11px] uppercase tracking-[0.32em] text-[#F9F8F6]/80">
                {t.eyebrow}
              </div>

              {/* Casa Alta — promotional display */}
              <h1
                data-testid="hero-title"
                className="mt-5 display text-[#F9F8F6] text-[92px] md:text-[168px] lg:text-[208px]"
              >
                {t.title1}<span className="text-[color:var(--alta-red-soft)]">{t.title2}</span>
                <span className="text-[color:var(--alta-red)]">.</span>
              </h1>

              {/* Poetic subline */}
              <div className="mt-4 serif text-[24px] md:text-[32px] leading-tight text-[#F9F8F6]/90 max-w-[36ch] italic">
                {t.subline_prefix}
                <span className="text-[color:var(--alta-red-soft)]">{t.subline_accent}</span>
              </div>

              {/* Info strip — data / local / formato */}
              <div
                data-testid="hero-info-strip"
                className="mt-10 grid grid-cols-3 gap-4 md:gap-10 max-w-[720px] border-t border-[#F9F8F6]/25 pt-6"
              >
                <InfoCell
                  tag={t.data_tag}
                  value={t.data_value}
                  sub={t.data_sub}
                  testid="info-data"
                />
                <InfoCell
                  tag={t.local_tag}
                  value={t.local_value}
                  sub={t.local_sub}
                  testid="info-local"
                />
                <InfoCell
                  tag={t.formato_tag}
                  value={t.formato_value}
                  sub={t.formato_sub}
                  testid="info-formato"
                />
              </div>

              {/* CTAs */}
              <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-5">
                <a
                  href="/convidado"
                  data-testid="hero-primary-cta"
                  className="btn-red-primary group"
                >
                  <span>{t.cta_primary}</span>
                  <span className="ml-2 transition-transform duration-300 group-hover:translate-x-1">→</span>
                </a>
                <a
                  href={`mailto:${t.cta_email}?subject=Candidatura%20Casa%20Alta%202026`}
                  data-testid="hero-secondary-cta"
                  className="mono text-[11px] uppercase tracking-[0.28em] text-[#F9F8F6] hover:text-[color:var(--alta-red-soft)] transition-colors border-b border-[#F9F8F6]/40 hover:border-[color:var(--alta-red-soft)] pb-1"
                >
                  {t.cta_secondary}
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll cue */}
        <button
          onClick={onScrollDown}
          data-testid="hero-scroll"
          className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3 mono text-[10px] uppercase tracking-[0.28em] text-[#F9F8F6]/70 hover:text-[#F9F8F6] transition-colors"
          aria-label="Descer"
        >
          <span>{t.scroll_cue}</span>
          <span className="w-px h-8 bg-[#F9F8F6]/50 animate-pulse" />
        </button>
      </div>
    </section>
  );
}

// Small info cell for the hero strip
function InfoCell({ tag, value, sub, testid }) {
  return (
    <div data-testid={testid}>
      <div className="mono text-[9px] uppercase tracking-[0.28em] text-[color:var(--alta-red-soft)]">
        {tag}
      </div>
      <div className="mt-2 serif text-[24px] md:text-[32px] leading-none text-[#F9F8F6]">
        {value}
      </div>
      <div className="mt-1 mono text-[10px] uppercase tracking-[0.24em] text-[#F9F8F6]/60">
        {sub}
      </div>
    </div>
  );
}

// ---------- MANIFESTO ----------
function Manifesto() {
  const { content } = useContent();
  const t = content.manifesto;
  return (
    <section
      data-testid="manifesto"
      className="py-32 md:py-44 bg-[#F9F8F6]"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-3">
          <div className="eyebrow">{t.eyebrow}</div>
          <div className="mt-6 mono text-[11px] uppercase tracking-[0.24em] text-neutral-500">
            {t.date_note}
          </div>
        </div>
        <div className="md:col-span-8 md:col-start-5">
          <p className="serif text-[30px] md:text-[44px] leading-[1.15] text-neutral-900 tracking-[-0.01em]">
            {t.body_pre}
            <em className="italic text-[color:var(--alta-red)]">{t.body_accent}</em>
            {t.body_post}
          </p>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-10">
            {[
              { k: t.stat1_k, v: t.stat1_v },
              { k: t.stat2_k, v: t.stat2_v },
              { k: t.stat3_k, v: t.stat3_v },
            ].map((s) => (
              <div key={s.v} data-testid={`stat-${s.k}`}>
                <div className="serif text-[56px] leading-none text-[color:var(--alta-red)]">{s.k}</div>
                <div className="mt-3 hairline" />
                <div className="mt-3 mono text-[10px] uppercase tracking-[0.28em] text-neutral-600">
                  {s.v}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// ---------- THE CAST (creators / humanização) ----------
export function TheCast() {
  const { content } = useContent();
  const t = content.cast;
  return (
    <section
      data-testid="the-cast"
      className="py-32 md:py-40 bg-[#F9F8F6] border-t border-neutral-200"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-4">
          <div className="flex items-center gap-3 mono text-[10px] uppercase tracking-[0.28em] text-[color:var(--alta-red)]">
            <span className="rec-dot" aria-hidden="true" />
            <span>{t.eyebrow}</span>
          </div>
          <h2 className="mt-8 serif text-[40px] md:text-[54px] leading-[1] tracking-[-0.01em] text-neutral-900">
            {t.title1}<br />
            {t.title2}<br />
            <em className="italic text-[color:var(--alta-red)]">{t.title_accent}</em>
          </h2>
          <p className="mt-8 max-w-[38ch] text-[15px] leading-[1.7] text-neutral-600 font-light">
            {t.body}
          </p>
          <div className="mt-10 flex items-center gap-3 mono text-[10px] uppercase tracking-[0.28em] text-neutral-500">
            <span className="w-8 h-px bg-neutral-400" />
            <span>{t.footnote}</span>
          </div>
        </div>

        {/* Editorial gallery — dynamic (unlimited creators, editable via admin) */}
        <div className="md:col-span-8 md:col-start-5 grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-5">
          {getCreators(t).map((creator, i) => {
            const { src, handle, vibe } = creator;
            const isReveal = !!creator.reveal || !src;
            // Staggered offset pattern (repeats every 6): 0,+10,-6,0,+10,-6...
            const mod = i % 6;
            const offset =
              mod === 1 || mod === 4 ? "md:mt-10" : mod === 2 || mod === 5 ? "md:-mt-6" : "";
            return (
              <figure
                key={creator.id || i}
                data-testid={`cast-${i}`}
                className={`relative grain-hover overflow-hidden bg-neutral-900 aspect-[3/4] ${offset}`}
              >
                {isReveal ? (
                  <div className="absolute inset-0 flex flex-col justify-between p-5 md:p-6 bg-[#1A1A1A]">
                    <div className="absolute inset-0 flex items-center justify-center opacity-[0.06]">
                      <img
                        src="https://customer-assets.emergentagent.com/job_shambala-retreat/artifacts/81id61ic_Paulo%20Fagotti%20-%20Logo%20Preto.png"
                        alt=""
                        className="w-2/3 h-auto"
                      />
                    </div>
                    <div className="relative mono text-[9px] uppercase tracking-[0.24em] text-[#F9F8F6]/60 flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-[color:var(--alta-red)]" />
                      <span>Confidencial</span>
                    </div>
                    <div className="relative">
                      <div className="serif text-[#F9F8F6] text-[30px] md:text-[38px] leading-none">
                        Reveal<br />
                        <em className="italic text-[color:var(--alta-red-soft)]">em breve.</em>
                      </div>
                      <div className="mt-5 mono text-[10px] uppercase tracking-[0.28em] text-[#F9F8F6]/70">
                        {vibe}
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    <img src={src} alt={`${handle} — ${vibe}`} className="w-full h-full object-cover" loading="lazy" />
                    <div className="absolute inset-x-0 bottom-0 p-4 md:p-5 bg-gradient-to-t from-black/75 via-black/25 to-transparent">
                      <div className="mono text-[11px] uppercase tracking-[0.24em] text-[#F9F8F6]">{handle}</div>
                      <div className="mt-1 mono text-[9px] uppercase tracking-[0.28em] text-[#F9F8F6]/70">{vibe}</div>
                    </div>
                    <div className="absolute top-3 right-3 mono text-[9px] uppercase tracking-[0.24em] text-[#F9F8F6]/90 flex items-center gap-1.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-[color:var(--alta-red)]" />
                      <span>Alta</span>
                    </div>
                  </>
                )}
              </figure>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ---------- SHOT LIST (creator deliverables) ----------
export function ShotList() {
  const { content } = useContent();
  const t = content.shotlist;
  const items = [
    { n: t.i1_n, label: t.i1_label, sub: t.i1_sub },
    { n: t.i2_n, label: t.i2_label, sub: t.i2_sub },
    { n: t.i3_n, label: t.i3_label, sub: t.i3_sub },
    { n: t.i4_n, label: t.i4_label, sub: t.i4_sub },
    { n: t.i5_n, label: t.i5_label, sub: t.i5_sub },
    { n: t.i6_n, label: t.i6_label, sub: t.i6_sub },
  ];
  return (
    <section
      data-testid="shot-list"
      className="relative py-32 md:py-40 bg-[#1A1A1A] text-[#F9F8F6] overflow-hidden border-y border-[#F9F8F6]/10"
    >
      <div className="absolute -right-24 -top-16 opacity-[0.05] pointer-events-none select-none hidden md:block">
        <Logo variant="light" size={520} testid="shotlist-watermark" />
      </div>
      <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-4">
          <div className="flex items-center gap-3 mono text-[10px] uppercase tracking-[0.28em] text-[color:var(--alta-red-soft)]">
            <span className="rec-dot" aria-hidden="true" />
            <span>{t.eyebrow}</span>
          </div>
          <h2 className="mt-8 serif text-[40px] md:text-[54px] leading-[1] tracking-[-0.01em]">
            {t.title}
          </h2>
          <p className="mt-8 max-w-[42ch] text-[13px] leading-[1.8] text-[#F9F8F6]/70 font-light">
            {t.body}
          </p>
        </div>
        <div className="md:col-span-7 md:col-start-6 grid grid-cols-2 md:grid-cols-3 gap-8">
          {items.map((s, i) => (
            <div
              key={i}
              data-testid={`shot-${i}`}
              className="border border-[#F9F8F6]/15 p-6 hover:border-[color:var(--alta-red)]/70 transition-colors duration-500"
            >
              <div className="serif text-[64px] leading-none text-[#F9F8F6]">
                <CountUp value={Number(s.n) || 0} testid={`shot-count-${i}`} />
              </div>
              <div className="mt-4 h-px bg-[color:var(--alta-red)]/60 w-8" />
              <div className="mt-4 text-[14px] text-[#F9F8F6]">{s.label}</div>
              <div className="mt-2 mono text-[10px] uppercase tracking-[0.24em] text-[#F9F8F6]/50">
                {s.sub}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ---------- SCENARIOS ----------
function Scenario({ item, index }) {
  const { content } = useContent();
  const t = content.scenarios;
  const flip = index % 2 === 1;
  const sceneNum = String(index + 1).padStart(2, "0");
  const takeNum = String(4 + index).padStart(2, "0");
  const tag = t[`s${index + 1}_tag`] || item.tag;
  const title = t[`s${index + 1}_title`] || item.title;
  const body = t[`s${index + 1}_body`] || item.body;
  return (
    <div
      data-testid={`scenario-${item.key}`}
      className="py-24 md:py-36 border-t border-neutral-200"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
        {/* Text */}
        <div className={`md:col-span-5 ${flip ? "md:order-2 md:col-start-8" : ""}`}>
          {/* Storyboard tag */}
          <div className="mono text-[10px] uppercase tracking-[0.28em] text-[color:var(--alta-red)] flex items-center gap-3" data-testid={`scene-tag-${item.key}`}>
            <span className="rec-dot" aria-hidden="true" />
            <span>Cena {sceneNum}</span>
            <span className="text-neutral-400">/</span>
            <span>Take {takeNum}</span>
          </div>
          <div className="eyebrow mt-4">{tag}</div>
          <h3 className="mt-8 serif text-[38px] md:text-[54px] leading-[1.05] tracking-[-0.01em] text-neutral-900">
            {title}
          </h3>
          <p className="mt-8 text-[15px] leading-[1.7] text-neutral-600 max-w-[46ch] font-light">
            {body}
          </p>
          <div className="mt-10 flex items-center gap-3 mono text-[10px] uppercase tracking-[0.28em] text-[#5A6B52]">
            <span className="w-8 h-px bg-[#5A6B52]" />
            <span>Curadoria Alta Agency</span>
          </div>
        </div>

        {/* Images: asymmetrical */}
        <div className={`md:col-span-6 ${flip ? "md:order-1 md:col-start-1" : "md:col-start-7"}`}>
          <div className="relative grain-hover">
            <img
              data-testid={`scenario-${item.key}-image-1`}
              src={IMAGES[item.images[0]]}
              alt={item.title}
              className="w-full h-[420px] md:h-[560px] object-cover"
            />
            <img
              data-testid={`scenario-${item.key}-image-2`}
              src={IMAGES[item.images[1]]}
              alt={item.title}
              className={`hidden md:block absolute w-[42%] h-[240px] object-cover shadow-[0_10px_40px_rgba(0,0,0,0.08)] ${
                flip ? "-right-8 -bottom-14" : "-left-8 -bottom-14"
              }`}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export function Scenarios() {
  const { content } = useContent();
  const t = content.scenarios;
  return (
    <section id="scenarios" data-testid="scenarios">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 pt-24 pb-4">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <div className="eyebrow">{t.eyebrow}</div>
          </div>
          <div className="md:col-span-7 md:col-start-6">
            <h2 className="serif text-[36px] md:text-[56px] leading-[1.05] tracking-[-0.01em] text-neutral-900">
              {t.title}
            </h2>
            <p className="mt-8 max-w-[52ch] text-[15px] leading-[1.7] text-neutral-600 font-light">
              {t.body}
            </p>
          </div>
        </div>
      </div>
      {SCENARIOS.map((s, i) => (
        <Scenario key={s.key} item={s} index={i} />
      ))}
    </section>
  );
}

// ---------- ACCESS GATE (inline on landing) ----------
function AccessGate() {
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setLoading(true);
    try {
      const res = await api.post("/access/verify", { code: code.trim() });
      localStorage.setItem("alta_guest_code", res.data.code);
      localStorage.setItem("alta_guest_label", res.data.label || "");
      toast.success("Acesso liberado");
      navigate("/convidado");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="gate"
      data-testid="access-gate"
      className="relative py-32 md:py-44 bg-[#F3F1EA] border-t border-neutral-200"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <div className="flex items-center gap-5">
            <Logo variant="dark" size={40} testid="gate-alta-mark" />
            <div className="eyebrow">Entrada oficial</div>
          </div>
          <h2 className="mt-8 serif text-[40px] md:text-[64px] leading-[1] tracking-[-0.01em] text-neutral-900">
            Sua presença<br />
            <em className="italic text-[color:var(--alta-red)]">é reservada.</em>
          </h2>
          <p className="mt-8 max-w-[42ch] text-[15px] leading-[1.7] text-neutral-600 font-light">
            O acesso ao cronograma, endereço e horários é restrito a criadores
            selecionados pela Alta Agency. Insira o código enviado para você.
          </p>
        </div>
        <div className="md:col-span-6 md:col-start-7 self-center">
          <form onSubmit={submit} className="space-y-10">
            <div>
              <label className="mono text-[10px] uppercase tracking-[0.28em] text-neutral-500">
                Código de convidado
              </label>
              <input
                data-testid="access-code-input"
                type="text"
                autoComplete="off"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder="SHAMBALA · ALTA · VIP"
                className="field-line mt-4 serif !text-[28px] tracking-[0.14em] uppercase"
              />
            </div>
            <div className="flex items-center gap-6">
              <button
                data-testid="access-submit"
                type="submit"
                disabled={loading}
                className="btn-sharp"
              >
                {loading ? "Verificando..." : "Acessar Programação"}
              </button>
              <span className="mono text-[10px] uppercase tracking-[0.28em] text-neutral-500">
                Confidencial
              </span>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}

// ---------- FOOTER ----------
function Footer() {
  const { content } = useContent();
  const t = content.footer;
  return (
    <footer
      data-testid="footer"
      className="relative bg-[#1A1A1A] text-[#F9F8F6] py-24 overflow-hidden"
    >
      {/* Faint background logo mark */}
      <div className="absolute -right-16 -bottom-24 opacity-[0.06] pointer-events-none select-none hidden md:block">
        <Logo variant="light" size={480} testid="footer-alta-watermark" />
      </div>

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-6">
          <Logo variant="light" size={96} testid="footer-alta-logo" />
          <div className="mt-8 serif text-[32px] leading-none">{t.title}</div>
          <div className="mt-3 mono text-[10px] uppercase tracking-[0.28em] text-[#F9F8F6]/60">
            {t.tagline}
          </div>
          <p className="mt-8 text-[13px] leading-[1.8] text-[#F9F8F6]/70 max-w-[44ch] font-light">
            {t.body}
          </p>
        </div>
        <div className="md:col-span-3 md:col-start-8 mono text-[11px] uppercase tracking-[0.24em] text-[#F9F8F6]/70 space-y-4">
          <div>{t.curadoria_label}</div>
          <div className="text-[#F9F8F6]">{t.curadoria_value}</div>
        </div>
        <div className="md:col-span-2 mono text-[11px] uppercase tracking-[0.24em] text-[#F9F8F6]/70 space-y-4">
          <div>{t.local_label}</div>
          <div className="text-[#F9F8F6]">{t.local_value}</div>
        </div>
        {/* Discreet admin link */}
        <div className="md:col-span-12 flex items-center justify-end pt-8 border-t border-[#F9F8F6]/10 mt-4">
          <a
            href="/admin/login"
            data-testid="footer-admin-link"
            className="mono text-[9px] uppercase tracking-[0.32em] text-[#F9F8F6]/30 hover:text-[color:var(--alta-red-soft)] transition-colors"
          >
            · admin ·
          </a>
        </div>
      </div>
    </footer>
  );
}

// ---------- LANDING ----------
export default function Landing() {
  const scenariosRef = useRef(null);

  useEffect(() => {
    // Preload hero
    const img = new Image();
    img.src = IMAGES.hero;
  }, []);

  const scrollDown = () => {
    scenariosRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div data-testid="landing" className="bg-[#F9F8F6] text-neutral-900">
      <Hero onScrollDown={scrollDown} />
      <ProductionMarquee testid="production-marquee" />
      <Manifesto />
      <TheCast />
      <ShotList />
      <div ref={scenariosRef}>
        <Scenarios />
      </div>
      <RealityTeaser />
      <AccessGate />
      <Footer />
    </div>
  );
}
